import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React from 'react';

import { ActionPermissions, CustomPermission } from '../../@types';
import { Link } from '../../components/atoms/Link';
import { isUserHasPermission } from '../../configs/permissions';

export interface ServicesV4HeaderProps {
  total: number;
  permissions: CustomPermission[] | undefined;
  exportAnchor: HTMLElement | null;
  onExportClick: (event: React.MouseEvent<HTMLElement>) => void;
  onExportClose: () => void;
  onExportCsv: () => void;
  onExportExcel: () => void;
  onRecentJobsClick: (event: React.MouseEvent<HTMLElement>) => void;
  onColumnsClick: () => void;
}

export const ServicesV4Header: React.FC<ServicesV4HeaderProps> = ({
  total,
  permissions,
  exportAnchor,
  onExportClick,
  onExportClose,
  onExportCsv,
  onExportExcel,
  onRecentJobsClick,
  onColumnsClick,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '20px', pt: '14px', flexWrap: 'wrap', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: theme.palette.text.primary }}>
          Jobs
        </Typography>
        {total > 0 && (
          <Box
            component="span"
            sx={{
              fontSize: '12px',
              color: 'text.secondary',
              fontWeight: 400,
              bgcolor: 'v4.border',
              px: 1,
              py: '2px',
              borderRadius: '10px',
            }}
          >
            {total.toLocaleString()} results
          </Box>
        )}
        <IconButton
          size="small"
          aria-label="Recent jobs"
          onClick={onRecentJobsClick}
          sx={{ color: theme.palette.text.secondary }}
        >
          <AccessTimeIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <MuiButton
          variant="outlined"
          size="small"
          startIcon={<ViewColumnOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={onColumnsClick}
          sx={{ textTransform: 'none', fontSize: '13px', borderColor: 'v4.border', color: 'text.primary', bgcolor: 'background.paper', '&:hover': { borderColor: 'v4.borderDark', bgcolor: 'v4.tableEven' } }}
        >
          Columns
        </MuiButton>
        <MuiButton
          variant="outlined"
          size="small"
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={onExportClick}
          sx={{ textTransform: 'none', fontSize: '13px', borderColor: 'v4.border', color: 'text.primary', bgcolor: 'background.paper', '&:hover': { borderColor: 'v4.borderDark', bgcolor: 'v4.tableEven' } }}
        >
          Export
        </MuiButton>
        <Menu
          anchorEl={exportAnchor}
          open={Boolean(exportAnchor)}
          onClose={onExportClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={onExportCsv}>Export CSV</MenuItem>
          <MenuItem onClick={onExportExcel}>Export Excel</MenuItem>
        </Menu>
        {isUserHasPermission(ActionPermissions.Service_Create, permissions) && (
          <Link href="/services/create">
            <MuiButton
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{ textTransform: 'none', fontSize: '13px', bgcolor: 'v4.accent', '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 } }}
            >
              New Job
            </MuiButton>
          </Link>
        )}
      </Box>
    </Box>
  );
};
